import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";

export interface DailyBrief {
  id: string;
  content: string;
  brief_date: string;
  language: string;
}

interface UseDailyBriefResult {
  brief: DailyBrief | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<unknown>;
}

/**
 * Daily Brief V1 — Calendar FASE 2 sub-bloco 3a.
 *
 * Cache strategy (22/05 rewrite — kill the regeneration-on-nav bug):
 *
 *   1. React Query owns the per-session cache. queryKey is
 *      ['daily-brief', userId, lang, todayYmd] with staleTime: Infinity,
 *      so navigating Home → Modules → Home does NOT refetch (data stays
 *      fresh within the day for the same user + language).
 *
 *   2. queryFn FIRST tries a direct SELECT on public.daily_briefs (RLS
 *      already allows `auth.uid() = user_id`). When the brief was
 *      generated earlier today, this is one round-trip to Postgres
 *      (~50-150ms) instead of a full edge-function cold start
 *      (~600-1500ms even in the cached path).
 *
 *   3. Only when no row exists for today we invoke
 *      daily-brief-generate, which generates with Claude, persists, and
 *      returns the brief. The edge fn is itself idempotent for the
 *      same (user_id, brief_date), so two browser tabs racing don't
 *      double-generate.
 *
 *   4. Day rollover is handled by the queryKey itself: as soon as
 *      `todayYmd` changes (next local day), the key mutates and React
 *      Query refetches naturally.
 *
 * Out of scope:
 *   - Cross-session persistence (no localStorage). React Query state
 *     dies with the tab, but the next mount lands on the fast DB-SELECT
 *     path because the brief is already on `daily_briefs`.
 *   - Logout: when userId changes the queryKey mutates and React Query
 *     refetches under the new identity. The old key's data is
 *     garbage-collected after gcTime.
 *
 * The legacy configurable-news-brief feature that previously owned this
 * name was removed in commit e780aab.
 */

function todayYmdLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

async function fetchOrGenerateBrief(
  userId: string,
  lang: string,
  todayYmd: string,
): Promise<DailyBrief> {
  // Fast path — direct SELECT on the table. RLS-scoped to the caller.
  const { data: existing, error: selectErr } = await supabase
    .from("daily_briefs")
    .select("id, content, brief_date, language")
    .eq("user_id", userId)
    .eq("brief_date", todayYmd)
    .maybeSingle();

  if (selectErr) {
    // Fall through to the edge fn — a transient DB error shouldn't kill
    // the card if the edge fn can still generate.
    console.warn("[useDailyBrief] direct select failed, falling through:", selectErr);
  }

  if (existing) {
    return existing as DailyBrief;
  }

  // Slow path — generate via edge fn. The edge fn is idempotent on
  // (user_id, brief_date); concurrent calls land on the same row.
  const timezone =
    (typeof Intl !== "undefined" &&
      Intl.DateTimeFormat &&
      Intl.DateTimeFormat().resolvedOptions().timeZone) ||
    "America/Sao_Paulo";

  const { data, error: invokeErr } = await supabase.functions.invoke(
    "daily-brief-generate",
    {
      body: { user_id: userId, language: lang, timezone },
    },
  );
  if (invokeErr) {
    throw new Error(invokeErr.message || "invoke_failed");
  }
  const payload = data as { brief?: DailyBrief; error?: string } | null;
  if (!payload || payload.error || !payload.brief) {
    throw new Error(payload?.error || "no_brief");
  }
  return payload.brief;
}

export function useDailyBrief(): UseDailyBriefResult {
  const { lang } = useLanguage();
  const todayYmd = useMemo(() => todayYmdLocal(), []);

  // We need userId in the queryKey so different users in the same tab
  // don't share a cached brief (rare but possible: account switch).
  // Probing via a tiny query that resolves synchronously enough not to
  // block the main flow.
  const { data: userId } = useQuery({
    queryKey: ["auth-user-id"],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data?.user?.id ?? null;
    },
    staleTime: Infinity,
  });

  const query = useQuery<DailyBrief, Error>({
    queryKey: ["daily-brief", userId, lang, todayYmd],
    queryFn: () => fetchOrGenerateBrief(userId as string, lang, todayYmd),
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24, // 24h
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  return {
    brief: query.data ?? null,
    loading: query.isPending && !!userId,
    error: query.error ? query.error.message : null,
    refresh: () => query.refetch(),
  };
}

export default useDailyBrief;
